import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { CircleCheck, Plus } from 'lucide-react';
import {
  JIRA_MAPPABLE_STATUSES,
  PRODUCT_TYPES,
  type AdminProduct,
  type JiraMappingRow,
  type ProductType,
  type RequestStatus,
} from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { PageTitle } from '@/components/portal/page-title';
import { Pill } from '@/components/portal/pill';
import { SectionCard } from '@/components/portal/section-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { JiraFieldsCard } from '@/features/admin/jira-fields-card';
import { Field, inputClass, NativeSelect } from '@/features/requests/request-form';
import { useAdminProducts, useBoardStatuses, useJiraBoards, useMondayBoards, useMondayStatuses, useSaveMapping, useSaveProduct } from '@/hooks/use-admin';
import { ApiValidationError } from '@/lib/api';
import { formatDayMonth, shortName } from '@/lib/format';
import { PRODUCT_TYPE_LABELS, STATUS_LABELS, VERSION_STATE_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';

interface ProductDraft {
  name: string;
  type: ProductType;
  ownerIds: string[];
  isActive: boolean;
  /** ADR-015: a product may follow its requests in Jira, in Monday, or in both. */
  useJira: boolean;
  useMonday: boolean;
  jiraBoardId: string;
  mondayBoardId: string;
  mondayStatusColumnId: string;
  requiresCmsArticleUrl: boolean;
  requiresClientInfo: boolean;
}

const draftOf = (p: AdminProduct | null): ProductDraft => ({
  name: p?.name ?? '',
  type: p?.type ?? 'transverse',
  ownerIds: p?.owners.map((o) => o.id) ?? [],
  isActive: p?.isActive ?? true,
  useJira: Boolean(p?.jiraBoardId),
  useMonday: Boolean(p?.mondayBoardId),
  jiraBoardId: p?.jiraBoardId ? String(p.jiraBoardId) : '',
  mondayBoardId: p?.mondayBoardId ?? '',
  mondayStatusColumnId: p?.mondayStatusColumnId ?? '',
  requiresCmsArticleUrl: p?.requiresCmsArticleUrl ?? false,
  requiresClientInfo: p?.requiresClientInfo ?? false,
});

function MappingState({ product }: { product: AdminProduct }) {
  const states = [
    product.jiraBoardId ? { tool: 'Jira', unmapped: product.unmappedStatuses } : null,
    product.mondayBoardId ? { tool: 'Monday', unmapped: product.mondayUnmappedStatuses } : null,
  ].filter((x): x is { tool: string; unmapped: string[] | null } => x !== null);
  if (states.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-col items-start gap-1">
      {states.map(({ tool, unmapped }) =>
        unmapped === null ? (
          <span key={tool} className="text-muted-foreground">
            {tool} indisponible
          </span>
        ) : unmapped.length === 0 ? (
          <Pill key={tool} className="bg-status-delivered text-status-delivered-foreground">
            {tool} · complète
          </Pill>
        ) : (
          <Pill key={tool} className="bg-warning text-warning-foreground">{`${tool} · ${unmapped.length} statut${unmapped.length > 1 ? 's' : ''} à associer`}</Pill>
        ),
      )}
    </span>
  );
}

type Mapping = Record<string, RequestStatus | ''>;

/** « Correspondance des statuts » of one tool: its statuses (Jira) or labels (Monday) → portal statuses. */
function MappingEditor({ tool, statuses, mapping, setMapping }: { tool: 'jira' | 'monday'; statuses: string[]; mapping: Mapping; setMapping: (update: (m: Mapping) => Mapping) => void }) {
  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      <legend className="mb-1 p-0 text-sm font-bold">Correspondance des statuts {tool === 'jira' ? 'Jira' : 'Monday'}</legend>
      <span className="text-xs text-muted-foreground">
        Statut {tool === 'monday' ? "de l'item Monday" : 'du ticket Jira'} → statut de la demande. Un statut « Ne pas suivre » ne fait pas bouger la demande.
      </span>
      {statuses.map((s) => (
        <div key={s} className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1.2fr)] items-center gap-2 text-[13px]">
          <span className="font-bold">{s}</span>
          <span aria-hidden="true">→</span>
          <NativeSelect id={`map-${tool}-${s}`} aria-label={`Statut portail pour ${s}`} value={mapping[s] ?? ''} onChange={(e) => setMapping((m) => ({ ...m, [s]: e.target.value as RequestStatus | '' }))} className="min-h-9 text-[13px]">
            <option value="">Ne pas suivre</option>
            {JIRA_MAPPABLE_STATUSES.map((p) => (
              <option key={p} value={p}>
                {STATUS_LABELS[p]}
              </option>
            ))}
          </NativeSelect>
        </div>
      ))}
    </fieldset>
  );
}

/** The saved rows when the board did not change, « Ne pas suivre » otherwise. */
function useMapping(statuses: string[] | undefined, same: boolean, saved: JiraMappingRow[]) {
  const [mapping, setMapping] = useState<Mapping>({});
  const savedKey = JSON.stringify(same ? saved : []);
  useEffect(() => {
    const rows = JSON.parse(savedKey) as JiraMappingRow[];
    setMapping(Object.fromEntries((statuses ?? []).map((s) => [s, rows.find((m) => m.jiraStatus === s)?.portalStatus ?? ''])));
  }, [statuses, savedKey]);
  return [mapping, setMapping] as const;
}

const rowsOf = (mapping: Mapping): JiraMappingRow[] => Object.entries(mapping).map(([jiraStatus, portalStatus]) => ({ jiraStatus, portalStatus: portalStatus || null }));

function ProductForm({ product, pos, jiraAvailable, mondayAvailable, onDone }: { product: AdminProduct | null; pos: { id: string; firstName: string; lastName: string }[]; jiraAvailable: boolean; mondayAvailable: boolean; onDone: () => void }) {
  const save = useSaveProduct();
  const saveMapping = useSaveMapping();
  const [draft, setDraft] = useState<ProductDraft>(draftOf(product));
  const boards = useJiraBoards(jiraAvailable && draft.useJira);
  const mondayBoards = useMondayBoards(mondayAvailable && draft.useMonday);
  const boardId = draft.useJira && draft.jiraBoardId ? Number(draft.jiraBoardId) : null;
  const mondayBoard = draft.useMonday ? ((mondayBoards.data ?? []).find((b) => b.id === draft.mondayBoardId) ?? null) : null;
  // A board with a single status column needs no choice.
  const mondayColumnId = draft.mondayStatusColumnId || (mondayBoard?.statusColumns.length === 1 ? mondayBoard.statusColumns[0]!.id : '');
  const mondayBoardId = draft.useMonday && draft.mondayBoardId ? draft.mondayBoardId : null;
  const jiraStatuses = useBoardStatuses(boardId);
  const mondayStatuses = useMondayStatuses(mondayBoardId ?? '', mondayBoardId ? mondayColumnId : '');
  const [jiraMapping, setJiraMapping] = useMapping(boardId ? jiraStatuses.data : undefined, boardId === product?.jiraBoardId, product?.mapping ?? []);
  const [mondayMapping, setMondayMapping] = useMapping(
    mondayBoardId ? mondayStatuses.data : undefined,
    mondayBoardId === product?.mondayBoardId && mondayColumnId === product?.mondayStatusColumnId,
    product?.mondayMapping ?? [],
  );
  const errors = save.error instanceof ApiValidationError ? Object.fromEntries(save.error.issues.map((i) => [String(i.path[0]), i.message])) : {};
  const set = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  // A current PO who no longer carries the role stays listed, so that it can be unticked.
  const ownerChoices = [...pos, ...(product?.owners ?? []).filter((o) => !pos.some((p) => p.id === o.id))];

  return (
    <form
      id="produit"
      aria-labelledby="produit-titre"
      className="flex flex-col gap-4 rounded-card border border-border bg-card p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(
          {
            id: product?.id ?? null,
            input: {
              name: draft.name,
              type: draft.type,
              ownerIds: draft.ownerIds,
              isActive: draft.isActive,
              jiraBoardId: boardId,
              mondayBoardId,
              mondayStatusColumnId: mondayBoardId && mondayColumnId ? mondayColumnId : null,
              requiresCmsArticleUrl: draft.requiresCmsArticleUrl,
              requiresClientInfo: draft.requiresClientInfo,
            },
          },
          {
            onSuccess: async ({ id }) => {
              // Statuses absent from a mapping stay « à associer »; « Ne pas suivre » is an explicit row.
              if (boardId && jiraStatuses.data) await saveMapping.mutateAsync({ productId: id, tracker: 'jira', mappings: rowsOf(jiraMapping) });
              if (mondayBoardId && mondayStatuses.data) await saveMapping.mutateAsync({ productId: id, tracker: 'monday', mappings: rowsOf(mondayMapping) });
              onDone();
            },
          },
        );
      }}
    >
      <h2 id="produit-titre" className="m-0 text-base font-bold">
        {product ? product.name : 'Nouveau produit'}
      </h2>
      <Field id="nom" label="Nom" required error={errors.name}>
        <input id="nom" value={draft.name} onChange={(e) => set('name', e.target.value)} className={inputClass} />
      </Field>
      <Field id="type" label="Type">
        <NativeSelect id="type" value={draft.type} onChange={(e) => set('type', e.target.value as ProductType)}>
          {PRODUCT_TYPES.map((t) => (
            <option key={t} value={t}>
              {PRODUCT_TYPE_LABELS[t]}
            </option>
          ))}
        </NativeSelect>
      </Field>
      {/* Several POs per product (ADR-010). The list offers people whose last login carried the PO role. */}
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-2 p-0 text-sm font-bold">PO</legend>
        {ownerChoices.length === 0 && <span className="text-xs text-muted-foreground">Aucune personne avec le rôle PO ne s'est encore connectée.</span>}
        {ownerChoices.map((p) => (
          <label key={p.id} className="flex items-center gap-2.5 text-[13px]">
            <input
              type="checkbox"
              checked={draft.ownerIds.includes(p.id)}
              onChange={(e) => set('ownerIds', e.target.checked ? [...draft.ownerIds, p.id] : draft.ownerIds.filter((id) => id !== p.id))}
            />
            {shortName(p)}
          </label>
        ))}
        {errors.ownerIds && <span className="text-xs text-destructive">{errors.ownerIds}</span>}
      </fieldset>

      {/* ADR-015: Jira, Monday, or both. */}
      <section className="flex flex-col gap-3 border-t border-border pt-4">
        <label className="flex items-center gap-2.5 text-sm font-bold">
          <input type="checkbox" checked={draft.useJira} disabled={!jiraAvailable && !draft.useJira} onChange={(e) => set('useJira', e.target.checked)} />
          Suivi dans Jira{jiraAvailable ? '' : ' (non configuré)'}
        </label>
        {draft.useJira && (
          <>
            <Field id="board" label="Board Jira" error={errors.jiraBoardId}>
              <NativeSelect id="board" value={draft.jiraBoardId} onChange={(e) => set('jiraBoardId', e.target.value)}>
                <option value="">Choisir un board</option>
                {(boards.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.projectKey} · {b.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            {jiraStatuses.data && (
              <span className="-mt-1 inline-flex items-center gap-1.5 text-xs text-status-delivered-foreground">
                <CircleCheck aria-hidden="true" className="size-3.5" /> Connexion vérifiée · {jiraStatuses.data.length} statuts dans le workflow
              </span>
            )}
            {boardId && jiraStatuses.data && <MappingEditor tool="jira" statuses={jiraStatuses.data} mapping={jiraMapping} setMapping={setJiraMapping} />}
          </>
        )}
      </section>

      <section className="flex flex-col gap-3 border-t border-border pt-4">
        <label className="flex items-center gap-2.5 text-sm font-bold">
          <input type="checkbox" checked={draft.useMonday} disabled={!mondayAvailable && !draft.useMonday} onChange={(e) => set('useMonday', e.target.checked)} />
          Suivi dans Monday{mondayAvailable ? '' : ' (non configuré)'}
        </label>
        {draft.useMonday && (
          <>
            <Field id="monday-board" label="Board Monday" error={errors.mondayBoardId}>
              <NativeSelect
                id="monday-board"
                value={draft.mondayBoardId}
                onChange={(e) => setDraft((d) => ({ ...d, mondayBoardId: e.target.value, mondayStatusColumnId: '' }))}
                disabled={mondayBoards.isPending}
              >
                <option value="">{mondayBoards.isPending ? 'Lecture des boards Monday…' : 'Choisir un board'}</option>
                {(mondayBoards.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                    {b.workspace ? ` · ${b.workspace}` : ''}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            {mondayBoard && mondayBoard.statusColumns.length > 1 && (
              <Field id="monday-colonne" label="Colonne de statut" error={errors.mondayStatusColumnId}>
                <NativeSelect id="monday-colonne" value={draft.mondayStatusColumnId} onChange={(e) => set('mondayStatusColumnId', e.target.value)}>
                  <option value="">Choisir la colonne</option>
                  {mondayBoard.statusColumns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            )}
            {product?.mondayBoardId && product.mondayBoardId === draft.mondayBoardId && !product.mondayHooked && (
              <span className="-mt-1 text-xs text-warning-foreground">Webhook absent : les changements faits dans Monday ne remontent pas. Enregistrez pour le créer.</span>
            )}
            {mondayBoardId && mondayStatuses.data && <MappingEditor tool="monday" statuses={mondayStatuses.data} mapping={mondayMapping} setMapping={setMondayMapping} />}
          </>
        )}
      </section>

      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-1 p-0 text-sm font-bold">Champs ajoutés au formulaire de demande</legend>
        <label className="flex items-center gap-2.5 text-[13px]">
          <input type="checkbox" checked={draft.requiresCmsArticleUrl} onChange={(e) => set('requiresCmsArticleUrl', e.target.checked)} /> URL de l'article dans le CMS (obligatoire)
        </label>
        <label className="flex items-center gap-2.5 text-[13px]">
          <input type="checkbox" checked={draft.requiresClientInfo} onChange={(e) => set('requiresClientInfo', e.target.checked)} /> Client concerné : email et NCCT (obligatoires)
        </label>
      </fieldset>
      <label className="flex items-center gap-2.5 text-[13px]">
        <input type="checkbox" checked={draft.isActive} onChange={(e) => set('isActive', e.target.checked)} /> Produit actif · proposé dans le formulaire
      </label>
      {product && (
        <p className="m-0 text-[13px] text-muted-foreground">
          Les versions de ce produit se gèrent dans{' '}
          <Link to="/produit/release-notes" className="font-bold">
            Espace produit › Release notes
          </Link>
          .
        </p>
      )}
      {(save.error ?? saveMapping.error) && !(save.error instanceof ApiValidationError) && (
        <p role="alert" className="m-0 text-sm font-bold text-destructive">
          {(save.error ?? saveMapping.error)!.message}
        </p>
      )}
      <div className="flex justify-between gap-2.5 border-t border-border pt-4">
        <Button type="button" variant="secondary" onClick={onDone}>
          Annuler
        </Button>
        <Button type="submit" disabled={save.isPending || saveMapping.isPending || !draft.name.trim()}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

/** 12 · Produits: catalogue, linked Jira board, mapping of its statuses, versions. Every change is audited. */
export function ProductsAdminPage() {
  const products = useAdminProducts();
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const data = products.data;
  const product = data?.items.find((p) => p.id === selected) ?? null;

  return (
    <>
      <PageTitle title="Produits" subtitle="Chaque produit est suivi dans un board Jira ou Monday. La correspondance des statuts du board vers ceux du portail se règle ici.">
        <Button type="button" onClick={() => setSelected('new')}>
          <Plus aria-hidden="true" className="size-4" /> Nouveau produit
        </Button>
      </PageTitle>
      <div className="flex flex-wrap items-start gap-6">
        <SectionCard className="flex-[999_1_560px]" title={`${data?.items.length ?? 0} produits`}>
          <Table minWidth={520}>
            <TableHeader>
              <TableRow>
                <TableHead>Produit</TableHead>
                <TableHead>Outil de suivi</TableHead>
                <TableHead>Version en cours</TableHead>
                <TableHead>Statuts Jira</TableHead>
                <TableHead>
                  <span className="sr-only">Action</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.items ?? []).map((p) => (
                <TableRow key={p.id} className={cn(p.id === selected && 'bg-accent', !p.isActive && 'text-muted-foreground')}>
                  <TableCell>
                    <span className="block font-bold">{p.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {PRODUCT_TYPE_LABELS[p.type]}
                      {p.owners.length > 0 && ` · PO ${p.owners.map(shortName).join(', ')}`}
                      {!p.isActive && ' · inactif'}
                    </span>
                  </TableCell>
                  <TableCell className="text-foreground-secondary">{p.jiraProjectKey ? `Jira · ${p.jiraProjectKey} · ${p.jiraBoardName ?? ''}` : p.mondayBoardId ? `Monday · ${p.mondayBoardName ?? p.mondayBoardId}` : 'Aucun'}</TableCell>
                  <TableCell className="whitespace-nowrap text-foreground-secondary">
                    {p.currentVersion
                      ? `${p.currentVersion.number} · ${p.currentVersion.state === 'in_production' ? 'livrée' : p.currentVersion.plannedDate ? formatDayMonth(p.currentVersion.plannedDate) : VERSION_STATE_LABELS[p.currentVersion.state].toLowerCase()}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <MappingState product={p} />
                  </TableCell>
                  <TableCell>
                    <button type="button" onClick={() => setSelected(p.id)} className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[13px] font-bold text-primary">
                      Configurer
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
        {selected && data && (
          <aside className="flex min-w-0 flex-[1_1_380px] flex-col gap-6">
            <ProductForm key={selected} product={product} pos={data.pos} jiraAvailable={data.jiraAvailable} mondayAvailable={data.mondayAvailable} onDone={() => setSelected(null)} />
            {product?.jiraBoardId && data.jiraAvailable && <JiraFieldsCard key={`jira-${product.id}-${product.jiraBoardId}`} productId={product.id} />}
          </aside>
        )}
      </div>
    </>
  );
}
